import { compareHuge, huge, type HugeNumber } from "@/shared/numbers";
import type {
  HoverIdleCumulativeStatsState,
  HoverIdleGridTier,
  HoverIdleHovTargetCellSnapshot,
  HoverIdleSnapshot,
  HoverIdleTimTargetCellSnapshot,
  HoverIdleUnlockState,
  HovGainUpgradeOneCostBatchSummary,
  HovGainUpgradeSimultaneousPurchasePreview,
  MHovSnapshot,
  MHovState,
  NHovState,
  OHovState,
} from "../types";
import {
  HOV_CELL_GAIN_MULTIPLIER_INCREMENT,
  HOV_CELL_UNLOCK_INITIAL_MULTIPLIER,
  type HovCellState,
  type HovTimBalanceState,
  type TimCellState,
} from "./hoverIdleState";
import {
  calculateUpgradeMilestoneMultiplier,
  calculateUpgradeMilestoneRemaining,
} from "./upgradeMilestones";
import { BHG_TO_HG_POWER_EXPONENT_BASE } from "./hovGainPipeline";
import { HOV_GRID_INCREASE_1_TIER } from "@/features/hoverIdle/unlocks/hoverIdleEffectUnlocks";
import { cloneMpmState, type MpmState } from "../mpm/mpmState";
import { getEffectiveHovGainUpgradeDirectMpAssignments } from "./directMpAssignments";
import { MP_WALL_DIRECT_MP_BLOCKED_REASON } from "../mpm/directMpBlockState";
import {
  canExecuteMHov,
  isMHovUnlocked,
  resolveMHovRewardMultiplier,
  shouldShowMHovButton,
} from "../mHov/mHovLogic";
import { createNHovSnapshot } from "../nHov/nHovLogic";
import type { NHovBranchGrowthExponentBonus } from "../nHov/nHovBranchEffects";
import { createOHovSnapshot } from "../oHov/oHovLogic";
import {
  createHovAmplifierSnapshot,
  type HovAmplifierApExponentBreakdown,
  type HovAmplifierEntryId,
  type HovAmplifierGaugeVisualSegments,
  type HovAmplifierRecentChargeSources,
  type HovAmplifierRecentDirectChargeAtMs,
  type HovAmplifierState,
} from "@/features/hoverIdle/hov/hovAmplifierModel";
import { type WallState } from "@/features/hoverIdle/wall/wallState";
import type { WallRewardCapLevel } from "@/features/hoverIdle/wall/wallRewardCap";
import { createWallSnapshot } from "@/features/hoverIdle/wall/wallSnapshot";
import {
  getFreshnessElapsedSinceHovRunMs,
  getFreshnessWallRewardExponentBonus,
  hasAmplificationWallClear,
  getStockWallNhovExecutionStock,
  hasStockWallFirstNhovBranchSelectionReward,
  isFreshnessWallActive,
  isHovGainUpgradeDirectMpBlockedByActiveWall,
  isNHovBranchBlockedByActiveWall,
  isHovTargetDirectMpBlockedByActiveWall,
  resolveActiveWallClearTargetHov,
  resolveActiveWallAmplifierChallenge,
  resolveHovCellUnlockBlockedWallId,
} from "@/features/hoverIdle/wall/wallChallengePolicy";
import type { ExplorationSnapshot } from "../exploration/explorationTypes";
import type {
  HovResetBalance,
  MHovBalance,
} from "@/features/hoverIdle/balance/hovResetBalanceTypes";

export type HoverIdleSnapshotSyncedState = {
  readonly unlocks: HoverIdleUnlockState;
};

export type HoverIdleSnapshotTransientState = {
  readonly hovCellClickWithinWindowCount: number;
};

/**
 * Result of the authoritative pre-snapshot mutation pass. Returned by the
 * model's `prepareSnapshotState()` and consumed read-only by the DTO builder.
 */
export interface HoverIdleSnapshotPreparation {
  readonly synced: HoverIdleSnapshotSyncedState;
  readonly transient: HoverIdleSnapshotTransientState;
}

export type HoverIdleSnapshotLightweightDerivations = {
  readonly effectiveHovGainPerSecond: HugeNumber;
  readonly effectiveHovGainPerSecondAfterUpgrade: HugeNumber;
  readonly hovUpgradeCost: HugeNumber;
  readonly hovUpgradeOneCostBatchPreview:
    | HovGainUpgradeOneCostBatchSummary
    | undefined;
  readonly hovUpgradeSimultaneousPurchasePreview:
    | HovGainUpgradeSimultaneousPurchasePreview
    | undefined;
  readonly canPurchaseHovUpgrade: boolean;
  readonly canPurchaseTimUpgrade: boolean;
};

export interface HoverIdleSnapshotBuildState {
  readonly hov: HugeNumber;
  readonly tim: HugeNumber;
  readonly fai: number;
  readonly timGainPerSecond: HugeNumber;
  readonly timUpgradeCost: HugeNumber;
  readonly timUpgradeGainIncrease: HugeNumber;
  readonly purchasedHovGainUpgrades: number;
  readonly isHoveringHov: boolean;
  readonly isHoveringTim: boolean;
  readonly hovTimBalance: HovTimBalanceState;
  readonly cumulativeStats: HoverIdleCumulativeStatsState;
  readonly hovGridTier: HoverIdleGridTier;
  readonly hovGridThemeHue: number | null;
  readonly hovHoveringCellIndex: number | null;
  readonly timHovConsumingCellIndex: number | null;
  readonly timHovConsumptionRatePerSecond: number;
  readonly hovCells: readonly HovCellState[];
  readonly minHovGainUnlockCostsByTargetIndex: readonly (HugeNumber | null)[];
  readonly minHovGainMilestoneTriggerCostsByMilestoneIndex: readonly (HugeNumber | null)[];
  readonly timGridTier: HoverIdleGridTier;
  readonly timHoveringCellIndex: number | null;
  readonly timCells: readonly TimCellState[];
  readonly mpCapacity: number;
  readonly mpmState: MpmState;
  readonly continuousPointerSeconds: number;
  readonly observerSeconds: number;
  readonly xPatternSeconds: number;
  readonly hPatternSeconds: number;
  readonly hovOLabelHoverSeconds: number;
  readonly isTimHovActivationEnabled: boolean;
  readonly resetBalance: HovResetBalance;
  readonly mHovState: MHovState;
  readonly nHovState: NHovState;
  readonly oHovState: OHovState;
  readonly oHovHovCoreGainPreview: HugeNumber;
  readonly outsideResetSerial: number;
  readonly hovAmplifierState: HovAmplifierState;
  readonly hovAmplifierRecentChargeSources: HovAmplifierRecentChargeSources;
  readonly hovAmplifierRecentDirectChargeAtMs: HovAmplifierRecentDirectChargeAtMs;
  readonly hovAmplifierGaugeVisualSegments: HovAmplifierGaugeVisualSegments;
  readonly hovAmplifierGainMultipliersByEntry: Partial<
    Record<HovAmplifierEntryId, HugeNumber>
  >;
  readonly hovAmplifierApExponentBreakdown: HovAmplifierApExponentBreakdown;
  readonly bhgToHgPowerExponentBonus: number;
  readonly hngRankScaleBaseBonus: number;
  // Skill-derived growth-exponent bonuses, threaded so MHov / NHov previews
  // match the model's authoritative reward and branch growth.
  readonly mhovGrowthExponentBonus: number;
  readonly nHovGrowthExponentBonus: NHovBranchGrowthExponentBonus;
  // True once `outside-mhov` / `outside-nhov` lift their hard Number-limit
  // caps. MHov preview growth remains subject to its post-boundary softcap.
  readonly mhovRewardUncapped: boolean;
  readonly nhovBranchEffectUncapped: boolean;
  readonly wallState: WallState;
  readonly wallUnlocked: boolean;
  readonly wallRewardCapLevel: WallRewardCapLevel;
  readonly nowMs: () => number;
}

export interface HoverIdleSnapshotHovCellOneCostSummaryOptions {
  readonly cellIndex: number;
  readonly cell: HovCellState;
  readonly upgradeAvailable: boolean;
  readonly effectiveCost: HugeNumber;
  // Final HG/EHG basis the actual Hov gain uses. Threaded so the simultaneous
  // purchase preview's value-after scales from the same basis as the real
  // gain rather than the BHG-stage `hovGainPerSecond`.
  readonly baseGainPerSecond?: HugeNumber;
}

export interface HoverIdleSnapshotReadHelpers {
  readonly resolveHovUpgradeGainIncrease: () => HugeNumber;
  readonly resolveEffectiveHovCellUpgradeCost: (
    cellIndex: number,
  ) => HugeNumber;
  readonly resolveHovCellGainPerSecond: (
    index: number,
    baseGainPerSecond?: HugeNumber,
  ) => HugeNumber;
  readonly resolveHovCellGainPerSecondWithMultiplier: (
    index: number,
    hovGainMultiplier: HugeNumber,
    enhancementLevel: number,
    baseGainPerSecond?: HugeNumber,
  ) => HugeNumber;
  readonly resolveHovCellOneCostBatchSummary: (
    options: HoverIdleSnapshotHovCellOneCostSummaryOptions,
  ) => HovGainUpgradeOneCostBatchSummary | undefined;
  readonly resolveHovCellSimultaneousPurchasePreview: (
    options: HoverIdleSnapshotHovCellOneCostSummaryOptions,
  ) => HovGainUpgradeSimultaneousPurchasePreview | undefined;
  readonly resolveTimCellGainPerSecond: (
    index: number,
    baseGainPerSecond?: HugeNumber,
  ) => HugeNumber;
  readonly resolveEffectiveBhgToHgPowerExponent: () => number;
}

export interface HoverIdleSnapshotBuildContext {
  readonly state: HoverIdleSnapshotBuildState;
  readonly synced: HoverIdleSnapshotSyncedState;
  readonly transient: HoverIdleSnapshotTransientState;
  readonly lightweight: HoverIdleSnapshotLightweightDerivations;
  readonly read: HoverIdleSnapshotReadHelpers;
  readonly exploration: ExplorationSnapshot;
}

export function buildHoverIdleSnapshot({
  state,
  synced,
  transient,
  lightweight,
  read,
  exploration,
}: HoverIdleSnapshotBuildContext): HoverIdleSnapshot {
  const hovTargetDirectMpBlockedByActiveWall =
    isHovTargetDirectMpBlockedByActiveWall(state.wallState);
  const hovGainUpgradeDirectMpBlockedByActiveWall =
    isHovGainUpgradeDirectMpBlockedByActiveWall(state.wallState);

  return {
    hov: state.hov,
    tim: state.tim,
    fai: state.fai,
    hovGainPerSecond: lightweight.effectiveHovGainPerSecond,
    hovGainPerSecondAfterUpgrade:
      lightweight.effectiveHovGainPerSecondAfterUpgrade,
    timGainPerSecond: state.timGainPerSecond,
    hovUpgradeCost: lightweight.hovUpgradeCost,
    hovUpgradeGainIncrease: read.resolveHovUpgradeGainIncrease(),
    hovUpgradeEnhancementLevel: state.purchasedHovGainUpgrades,
    hovUpgradeOneCostBatchPreview: lightweight.hovUpgradeOneCostBatchPreview,
    hovUpgradeSimultaneousPurchasePreview:
      lightweight.hovUpgradeSimultaneousPurchasePreview,
    hovUpgradeMilestoneMultiplier: calculateUpgradeMilestoneMultiplier(
      state.purchasedHovGainUpgrades,
    ),
    hovUpgradeNextMilestoneRemaining: calculateUpgradeMilestoneRemaining(
      state.purchasedHovGainUpgrades,
    ),
    timUpgradeCost: state.timUpgradeCost,
    timUpgradeGainIncrease: state.timUpgradeGainIncrease,
    isHoveringHov: state.isHoveringHov,
    isHoveringTim: state.isHoveringTim,
    canPurchaseHovUpgrade: lightweight.canPurchaseHovUpgrade,
    canPurchaseTimUpgrade: lightweight.canPurchaseTimUpgrade,
    hovTimBalance: { ...state.hovTimBalance },
    unlocks: { ...synced.unlocks },
    cumulativeStats: { ...state.cumulativeStats },
    hovGridTier: state.hovGridTier,
    hovGridThemeHue: state.hovGridThemeHue,
    hovHoveringCellIndex: state.hovHoveringCellIndex,
    hovTargetCells: buildHovTargetCellSnapshots({
      state,
      lightweight,
      read,
    }),
    minHovGainUnlockCostsByTargetIndex: [
      ...state.minHovGainUnlockCostsByTargetIndex,
    ],
    minHovGainMilestoneTriggerCostsByMilestoneIndex: [
      ...state.minHovGainMilestoneTriggerCostsByMilestoneIndex,
    ],
    timGridTier: state.timGridTier,
    timHoveringCellIndex: state.timHoveringCellIndex,
    timTargetCells: buildTimTargetCellSnapshots({ state, read }),
    mpCapacity: state.mpCapacity,
    mpm: cloneMpmState(state.mpmState),
    hovTargetDirectMpBlockedByActiveWall,
    ...(hovTargetDirectMpBlockedByActiveWall
      ? { hovTargetDirectMpBlockedReason: MP_WALL_DIRECT_MP_BLOCKED_REASON }
      : {}),
    hovGainUpgradeDirectMpBlockedByActiveWall,
    ...(hovGainUpgradeDirectMpBlockedByActiveWall
      ? {
          hovGainUpgradeDirectMpBlockedReason: MP_WALL_DIRECT_MP_BLOCKED_REASON,
        }
      : {}),
    effectiveHovGainUpgradeDirectMpAssignments: [
      ...getEffectiveHovGainUpgradeDirectMpAssignments(
        state.mpmState,
        state.wallState,
      ),
    ],
    continuousPointerSeconds: state.continuousPointerSeconds,
    observerSeconds: state.observerSeconds,
    xPatternSeconds: state.xPatternSeconds,
    hPatternSeconds: state.hPatternSeconds,
    hovOLabelHoverSeconds: state.hovOLabelHoverSeconds,
    hovCellClickWithinWindowCount: transient.hovCellClickWithinWindowCount,
    isTimHovActivationEnabled: state.isTimHovActivationEnabled,
    mHov: buildHoverIdleMHovSnapshot(state.mHovState, {
      exponent:
        state.resetBalance.mHov.rewardExponent +
        getFreshnessWallRewardExponentBonus(
          state.wallState,
          state.wallRewardCapLevel,
        ) +
        state.mhovGrowthExponentBonus,
      balance: state.resetBalance.mHov,
      freshnessStaling: isFreshnessWallActive(state.wallState)
        ? {
            elapsedSinceHovRunMs: getFreshnessElapsedSinceHovRunMs(
              state.wallState,
            ),
          }
        : undefined,
      mhovRewardUncapped: state.mhovRewardUncapped,
    }),
    nHov: createNHovSnapshot({
      nHovState: state.nHovState,
      balance: state.resetBalance.nHov,
      runBaseHov: state.mHovState.currentLoopHovEarned,
      hngRankScaleBaseBonus: state.hngRankScaleBaseBonus,
      growthExponentBonus: state.nHovGrowthExponentBonus,
      isBranchBlockedByActiveWall: (branchId) =>
        isNHovBranchBlockedByActiveWall(state.wallState, branchId),
      firstExecutionBranchSelectionUnlocked:
        hasStockWallFirstNhovBranchSelectionReward(state.wallState),
      executionStock: getStockWallNhovExecutionStock(state.wallState),
      uncapped: state.nhovBranchEffectUncapped,
      freshnessStaling: isFreshnessWallActive(state.wallState)
        ? {
            elapsedSinceHovRunMs: getFreshnessElapsedSinceHovRunMs(
              state.wallState,
            ),
          }
        : undefined,
    }),
    oHov: createOHovSnapshot({
      oHovState: state.oHovState,
      hov: state.hov,
      nowMs: state.nowMs(),
      hovCoreGainPreview: state.oHovHovCoreGainPreview,
      hovCoreGainSegmentsUnlocked: state.mpmState.ohovExecution.unlocked,
      manualExecutionUnlocked: state.mpmState.ohovExecution.unlocked,
      executeTargetHov: resolveActiveWallClearTargetHov(state.wallState),
    }),
    outsideResetSerial: state.outsideResetSerial,
    hovAmplifier: createHovAmplifierSnapshot(state.hovAmplifierState, {
      availableHovCore: state.oHovState.hovCore,
      recentChargeSources: state.hovAmplifierRecentChargeSources,
      recentDirectChargeAtMs: state.hovAmplifierRecentDirectChargeAtMs,
      visualSegments: state.hovAmplifierGaugeVisualSegments,
      nowMs: state.nowMs(),
      amplifierGainMultipliersByEntry: state.hovAmplifierGainMultipliersByEntry,
      availability: {
        amplificationWallCleared: hasAmplificationWallClear(state.wallState),
      },
      amplifierChallenge: resolveActiveWallAmplifierChallenge(state.wallState),
      apExponentBreakdown: state.hovAmplifierApExponentBreakdown,
    }),
    hovGainPower: {
      baseExponent: BHG_TO_HG_POWER_EXPONENT_BASE,
      sourceComposedExponentBonus: state.bhgToHgPowerExponentBonus,
      effectiveExponent: read.resolveEffectiveBhgToHgPowerExponent(),
    },
    wall: createWallSnapshot({
      state: state.wallState,
      wallUnlocked: state.wallUnlocked,
      rewardCapLevel: state.wallRewardCapLevel,
    }),
    exploration,
  };
}

export interface BuildHoverIdleMHovSnapshotOptions {
  // Effective MHov reward exponent (`MHOV_REWARD_EXPONENT` plus any wall
  // clear bonus and skill growth-exponent bonus). Defaults to the base when
  // omitted so preview / view callers outside the model can still produce a
  // stable snapshot.
  readonly exponent?: number;
  readonly balance?: MHovBalance;
  // Freshness Wall staling inputs. Present only when the wall is active so
  // the preview and execution paths align on the same effective value.
  readonly freshnessStaling?: {
    readonly elapsedSinceHovRunMs: number;
  };
  // When true (granted by `outside-mhov`), the preview is no longer hard-capped
  // at NUMBER_LIMIT_BOUNDARY, but the post-boundary softcap still applies.
  readonly mhovRewardUncapped?: boolean;
}

export function buildHoverIdleMHovSnapshot(
  state: MHovState,
  options: BuildHoverIdleMHovSnapshotOptions = {},
): MHovSnapshot {
  const { count, hgMultiplier, currentLoopHovEarned, maxHovSinceLastMHov } =
    state;
  const rewardBaseHov = currentLoopHovEarned;
  const isUnlocked =
    count > 0 ||
    isMHovUnlocked({ currentLoopHovEarned: rewardBaseHov }, options.balance);
  const freshnessWallActive = options.freshnessStaling !== undefined;
  const rewardResolution = resolveMHovRewardMultiplier(rewardBaseHov, {
    exponent: options.exponent,
    balance: options.balance,
    hardCapLifted: options.mhovRewardUncapped,
    freshnessStaling:
      options.freshnessStaling === undefined
        ? undefined
        : {
            currentMultiplier: hgMultiplier,
            elapsedSinceHovRunMs: options.freshnessStaling.elapsedSinceHovRunMs,
          },
  });
  const freshPendingMultiplier =
    rewardResolution.effectiveFreshRewardMultiplier;
  const effectivePendingMultiplier = rewardResolution.effectiveRewardMultiplier;
  const buttonStateInputs = {
    currentLoopHovEarned,
    currentMultiplier: hgMultiplier,
    freshRewardMultiplier: freshPendingMultiplier,
    effectiveRewardMultiplier: effectivePendingMultiplier,
  };
  return {
    count,
    hgMultiplier,
    pendingMultiplier: freshPendingMultiplier,
    effectivePendingMultiplier,
    isUnlocked,
    shouldShow: shouldShowMHovButton(buttonStateInputs, options.balance),
    canExecute: canExecuteMHov(buttonStateInputs, options.balance),
    freshnessWallActive,
    currentLoopHovEarned,
    maxHovSinceLastMHov,
  };
}

function buildHovTargetCellSnapshots({
  state,
  lightweight,
  read,
}: Pick<
  HoverIdleSnapshotBuildContext,
  "state" | "lightweight" | "read"
>): readonly HoverIdleHovTargetCellSnapshot[] {
  const rawMpAssignments = new Set(state.mpmState.hovTargets.assignments);
  return state.hovCells.map((cell, index) => {
    const upgradeAvailable = isHovCellUpgradeAvailable({
      index,
      hovGridTier: state.hovGridTier,
    });
    const wallBlockedId = cell.unlocked
      ? null
      : resolveHovCellUnlockBlockedWallId(state.wallState, index);
    const wallBlocked = wallBlockedId !== null;
    const nextMultiplierAfterUpgrade = cell.unlocked
      ? cell.hovGainMultiplier.add(HOV_CELL_GAIN_MULTIPLIER_INCREMENT)
      : huge(HOV_CELL_UNLOCK_INITIAL_MULTIPLIER);
    const enhancementLevel = resolveHovCellEnhancementLevel(cell, index);
    const effectiveCost = read.resolveEffectiveHovCellUpgradeCost(index);
    const effectiveHovGainPerSecondAfterUpgrade =
      read.resolveHovCellGainPerSecondWithMultiplier(
        index,
        nextMultiplierAfterUpgrade,
        enhancementLevel + 1,
        lightweight.effectiveHovGainPerSecond,
      );
    return {
      index,
      unlocked: cell.unlocked,
      timHovConsumptionRatePerSecond:
        state.timHovConsumingCellIndex === index
          ? state.timHovConsumptionRatePerSecond
          : 0,
      hovGainPerSecond: read.resolveHovCellGainPerSecond(
        index,
        lightweight.effectiveHovGainPerSecond,
      ),
      upgradeAvailable,
      ...(wallBlockedId !== null
        ? {
            upgradePurchaseBlockedReason: {
              type: "wall" as const,
              wallId: wallBlockedId,
            },
          }
        : {}),
      hovGainMultiplier: cell.hovGainMultiplier,
      upgradeCost: effectiveCost,
      enhancementLevel,
      upgradeOneCostBatchPreview: read.resolveHovCellOneCostBatchSummary({
        cellIndex: index,
        cell,
        upgradeAvailable,
        effectiveCost,
      }),
      upgradeSimultaneousPurchasePreview:
        read.resolveHovCellSimultaneousPurchasePreview({
          cellIndex: index,
          cell,
          upgradeAvailable,
          effectiveCost,
          baseGainPerSecond: lightweight.effectiveHovGainPerSecond,
        }),
      upgradeMilestoneMultiplier:
        calculateUpgradeMilestoneMultiplier(enhancementLevel),
      nextUpgradeMilestoneRemaining:
        calculateUpgradeMilestoneRemaining(enhancementLevel),
      purchasedUpgrades: cell.purchasedUpgrades,
      canPurchase:
        upgradeAvailable &&
        !wallBlocked &&
        compareHuge(state.hov, effectiveCost) >= 0,
      mpPlaced: rawMpAssignments.has(index),
      effectiveHovGainPerSecond: read.resolveHovCellGainPerSecond(
        index,
        lightweight.effectiveHovGainPerSecond,
      ),
      effectiveHovGainPerSecondAfterUpgrade,
    };
  });
}

function buildTimTargetCellSnapshots({
  state,
  read,
}: Pick<
  HoverIdleSnapshotBuildContext,
  "state" | "read"
>): readonly HoverIdleTimTargetCellSnapshot[] {
  return state.timCells.map((cell, index) => ({
    index,
    unlocked: cell.unlocked,
    timGainPerSecond: read.resolveTimCellGainPerSecond(
      index,
      state.timGainPerSecond,
    ),
    timGainMultiplier: cell.timGainMultiplier,
    upgradeCost: cell.upgradeCost,
    purchasedUpgrades: cell.purchasedUpgrades,
    canPurchase: compareHuge(state.tim, cell.upgradeCost) >= 0,
    mpPlaced: false,
  }));
}

function resolveHovCellEnhancementLevel(
  cell: HovCellState,
  index: number,
): number {
  if (!cell.unlocked) return 0;
  return cell.purchasedUpgrades + (index === 0 ? 1 : 0);
}

function isHovCellUpgradeAvailable(options: {
  readonly index: number;
  readonly hovGridTier: HoverIdleGridTier;
}): boolean {
  if (options.index === 0) {
    return options.hovGridTier >= HOV_GRID_INCREASE_1_TIER;
  }
  return options.index >= 0 && options.index < options.hovGridTier;
}
